import { SignupRental, VerifyEmail } from "@/pages/signup/Signups";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OAuthGate } from "@/components/GoogleAuth";
import { SuspensionModal, OutOfTerritoryModal } from "@/components/AuthModals";
import { GlobalAIFab, HelpFab, HomeFloatingButton } from "@/components/Floating";
import { ImpersonationBanner, InsuranceGate, LsoTrainingBanner } from "@/components/Banners";
import { EmployeeRouteGuard } from "@/components/RouteGuards";
import { homeRouteFor } from "@/lib/roles";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import ComingSoon from "@/pages/ComingSoon";
import Legal from "@/pages/Legal";
import ShopHome from "@/pages/shop/ShopHome";
import ShopProduct from "@/pages/shop/ShopProduct";
import ShopCart from "@/pages/shop/ShopCart";
import ShopThanks from "@/pages/shop/ShopThanks";
import AdminPublicStore from "@/pages/admin/AdminPublicStore";
import AdminLsoPreview from "@/pages/admin/AdminLsoPreview";
import AdminMarketingVideos from "@/pages/admin/AdminMarketingVideos";
import MarketingVideos from "@/pages/MarketingVideos";
import LsoHub from "@/pages/lso/LsoHub";
import LsoModule from "@/pages/lso/LsoModule";
import LsoFinalExam from "@/pages/lso/LsoFinalExam";
import LsoCertificate from "@/pages/lso/LsoCertificate";
import LsoEmployees from "@/pages/lso/LsoEmployees";
import MyLsoCert from "@/pages/lso/MyLsoCert";
import LsoRental from "@/pages/lso/LsoRental";
import LsoVerify from "@/pages/lso/LsoVerify";
import CleaningAgent from "@/pages/ai/CleaningAgent";
import { CleaningSettingsLibrary, MyPresets } from "@/pages/ai/CleaningSettingsPages";
import MachineCalibration from "@/pages/ai/MachineCalibration";
import VideoStudio from "@/pages/VideoStudio";
import SalesCoach from "@/pages/ai/SalesCoach";
import { PlaybookIndex, PlaybookSection } from "@/pages/content/Playbook";
import FranchiseSuccessGuide from "@/pages/content/FranchiseSuccessGuide";
import EmployeeSuccessGuide from "@/pages/content/EmployeeSuccessGuide";
import Operations from "@/pages/content/Operations";
import Handbook from "@/pages/content/Handbook";

import { StoreIndex, StoreProduct } from "@/pages/store/Store";
import StoreCart from "@/pages/store/StoreCart";
import StoreOrders from "@/pages/store/StoreOrders";
import { Warranty, Insurance } from "@/pages/compliance/Compliance";
import { SafetyCenter, JobsiteChecklist, IncidentReport } from "@/pages/compliance/Safety";
import QuickBooks from "@/pages/compliance/QuickBooks";
import { Community, CommunityQuestion } from "@/pages/community/Community";
import CustomerCommunity from "@/pages/community/CustomerCommunity";
import CustomerTeam from "@/pages/community/CustomerTeam";
import { CartProvider } from "@/context/CartContext";
import { DesktopShell } from "@/components/DesktopShell";
import { AdminHub, AdminSection } from "@/pages/admin/Admin";
import AdminConsole from "@/pages/admin/Console";
import Broadcast from "@/pages/admin/Broadcast";
import QuoteBuilderTracker from "@/pages/admin/QuoteBuilderTracker";
import JobberFixes from "@/pages/admin/JobberFixes";
import QuoteTemplates from "@/pages/admin/QuoteTemplates";

import AdminStore from "@/pages/admin/AdminStore";
import AdminFeedbackPage from "@/pages/admin/AdminFeedback";
import { Section, Support, ManualViewer } from "@/pages/misc/Resources";
import { Training, TrainingCourse, EmployeeOnboarding, LaunchTraining, LaunchDay } from "@/pages/misc/Training";
import { FeedbackFeed, NewFeedback, QuizScreen } from "@/pages/misc/Feedback";
import HealthCheck from "@/pages/misc/HealthCheck";
import Guide from "@/pages/misc/Guide";
import ManageEmployees from "@/pages/misc/Employees";
import QuoteBuilder from "@/pages/quote/QuoteBuilder";
import BillingHome from "@/pages/billing/BillingHome";
import BillingSetup from "@/pages/billing/BillingSetup";
import DocEditor from "@/pages/billing/DocEditor";
import JobEditor from "@/pages/billing/JobEditor";
import BillingCalendar from "@/pages/billing/Calendar";
import MySchedule from "@/pages/billing/MySchedule";
import PublicPay from "@/pages/billing/PublicPay";
import PublicChangeOrder from "@/pages/billing/PublicChangeOrder";
import CommCenter from "@/pages/billing/CommCenter";
import RequestForm from "@/pages/billing/RequestForm";
import AdminBilling from "@/pages/admin/AdminBilling";
import Agreements from "@/pages/admin/Agreements";
import BookkeepingHome from "@/pages/bookkeeping/BookkeepingHome";
import EntityMapping from "@/pages/bookkeeping/EntityMapping";
import BkConnections from "@/pages/bookkeeping/Connections";
import BkQboMapping from "@/pages/bookkeeping/QboMapping";
import BkTransactions from "@/pages/bookkeeping/Transactions";
import BkJobLog from "@/pages/bookkeeping/JobLog";
import BkMatching from "@/pages/bookkeeping/Matching";
import BkReview from "@/pages/bookkeeping/Review";
import BkRules from "@/pages/bookkeeping/Rules";
import BkReceipts from "@/pages/bookkeeping/Receipts";
import MyAgreement from "@/pages/MyAgreement";
import ProjectQuote from "@/pages/quote/ProjectQuote";

const GUIDE_ROLES = ["franchisee", "franchisee_employee", "admin"];
const BUYERS = ["franchisee", "admin"];
const OWNERS = ["machine_owner", "admin"];

const DASHBOARD_ROLES = ["franchisee", "franchisee_employee", "machine_owner", "admin"];
const ROLE_HOMES = {};
const LSO_STAFF = ["franchisee", "franchisee_employee", "machine_owner", "admin"];
const RENTAL = ["rental_customer", "admin"];

const Protected = ({ children, roles, feature }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#4CCC06]" /></div>;
  if (!user) return <Navigate to="/" replace state={{ from: pathname }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeRouteFor(user)} replace />;
  if (feature && user.role === "franchisee_employee" && user.employee_features?.[feature] === false) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicComingSoon = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0A0A0A] px-6 text-center font-body text-white" data-testid="public-coming-soon">
    <p className="text-[11px] font-bold tracking-[0.3em] text-[#4CCC06]">ARRIVING IN PHASE 5</p>
    <h1 className="font-display text-4xl font-extrabold uppercase">Sign-up is moving to the new site</h1>
    <p className="max-w-md text-sm text-neutral-400">New account sign-up is still handled by the current ALR portal while we rebuild. Need help? Email info@alrclean.com.</p>
    <a href="/" data-testid="public-coming-soon-back" className="mt-2 rounded-lg bg-[#4CCC06] px-6 py-3 text-[13px] font-extrabold tracking-wider text-black">BACK TO SIGN IN</a>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <CartProvider>
      <BrowserRouter>
        <OAuthGate>
        <ImpersonationBanner />
        <DesktopShell>
        <InsuranceGate>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/shop" element={<ShopHome />} />
          <Route path="/shop/c/:group" element={<ShopHome />} />
          <Route path="/shop/p/:id" element={<ShopProduct />} />
          <Route path="/shop/cart" element={<ShopCart />} />
          <Route path="/shop/thank-you" element={<ShopThanks />} />
          <Route path="/admin/public-store" element={<Protected roles={["admin"]}><AdminPublicStore /></Protected>} />
          <Route path="/admin/lso-course-preview" element={<Protected roles={["admin"]}><AdminLsoPreview /></Protected>} />
          <Route path="/admin/marketing-videos" element={<Protected roles={["admin"]}><AdminMarketingVideos /></Protected>} />
          <Route path="/marketing-videos" element={<Protected roles={DASHBOARD_ROLES}><MarketingVideos /></Protected>} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/dashboard" element={<Protected roles={DASHBOARD_ROLES}><Dashboard /></Protected>} />
          <Route path="/ai-assistant" element={<Protected roles={[...LSO_STAFF, "rental_customer"]} feature="ai"><CleaningAgent /></Protected>} />
          <Route path="/sales-coach" element={<Protected roles={["franchisee", "franchisee_employee", "admin"]} feature="sales_coach"><SalesCoach /></Protected>} />
          <Route path="/cleaning-settings" element={<Protected roles={[...LSO_STAFF, "rental_customer"]}><CleaningSettingsLibrary /></Protected>} />
          <Route path="/video-studio" element={<Protected roles={["franchisee", "franchisee_employee", "machine_owner", "admin"]} feature="video_studio"><VideoStudio /></Protected>} />
          <Route path="/my-presets" element={<Protected roles={["franchisee", "franchisee_employee", "admin"]} feature="my_presets"><MyPresets /></Protected>} />
          <Route path="/laser-machines" element={<Protected roles={["franchisee", "franchisee_employee", "admin"]} feature="my_presets"><MachineCalibration /></Protected>} />
          <Route path="/marketing-coach" element={<Navigate to="/sales-coach" replace />} />
          <Route path="/playbook" element={<Protected roles={GUIDE_ROLES} feature="playbook"><PlaybookIndex /></Protected>} />
          <Route path="/playbook/:playbook/:phase" element={<Protected roles={GUIDE_ROLES} feature="playbook"><PlaybookSection /></Protected>} />
          <Route path="/franchise-success-guide" element={<Protected roles={GUIDE_ROLES} feature="success_guide"><FranchiseSuccessGuide /></Protected>} />
          <Route path="/employee-success-guide" element={<Protected roles={GUIDE_ROLES} feature="employee_guide"><EmployeeSuccessGuide /></Protected>} />
          <Route path="/operations" element={<Protected roles={GUIDE_ROLES}><Operations /></Protected>} />
          <Route path="/handbook" element={<Protected roles={GUIDE_ROLES} feature="handbook"><Handbook /></Protected>} />
          <Route path="/signup-rental" element={<SignupRental />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminHub /></Protected>} />
          <Route path="/admin/console" element={<Protected roles={["admin"]}><AdminConsole /></Protected>} />
          <Route path="/admin/broadcast" element={<Protected roles={["admin"]}><Broadcast /></Protected>} />
          <Route path="/admin/quote-builder-tracker" element={<Protected roles={["admin"]}><QuoteBuilderTracker /></Protected>} />
          <Route path="/admin/jobber-fixes" element={<Protected roles={["admin"]}><JobberFixes /></Protected>} />
          <Route path="/admin/quote-templates" element={<Protected roles={["admin"]}><QuoteTemplates /></Protected>} />
          <Route path="/admin/store" element={<Protected roles={["admin"]}><AdminStore /></Protected>} />
          <Route path="/admin/feedback" element={<Protected roles={["admin"]}><AdminFeedbackPage /></Protected>} />
          <Route path="/admin/quickbooks" element={<Navigate to="/quickbooks" replace />} />
          <Route path="/admin/billing" element={<Protected roles={["admin"]}><AdminBilling /></Protected>} />
          <Route path="/admin/agreements" element={<Protected roles={["admin"]}><Agreements /></Protected>} />
          <Route path="/bookkeeping" element={<Protected roles={["admin"]}><BookkeepingHome /></Protected>} />
          <Route path="/bookkeeping/mapping" element={<Protected roles={["admin"]}><EntityMapping /></Protected>} />
          <Route path="/bookkeeping/connections" element={<Protected roles={["admin"]}><BkConnections /></Protected>} />
          <Route path="/bookkeeping/qbo-mapping" element={<Protected roles={["admin"]}><BkQboMapping /></Protected>} />
          <Route path="/bookkeeping/transactions" element={<Protected roles={["admin"]}><BkTransactions /></Protected>} />
          <Route path="/bookkeeping/jobs" element={<Protected roles={["admin"]}><BkJobLog /></Protected>} />
          <Route path="/bookkeeping/matching" element={<Protected roles={["admin"]}><BkMatching /></Protected>} />
          <Route path="/bookkeeping/review" element={<Protected roles={["admin"]}><BkReview /></Protected>} />
          <Route path="/bookkeeping/rules" element={<Protected roles={["admin"]}><BkRules /></Protected>} />
          <Route path="/bookkeeping/receipts" element={<Protected roles={["admin"]}><BkReceipts /></Protected>} />
          <Route path="/my-agreement" element={<Protected roles={["franchisee"]}><MyAgreement /></Protected>} />
          <Route path="/billing" element={<Protected roles={["franchisee"]}><BillingHome /></Protected>} />
          <Route path="/billing/setup" element={<Protected roles={["franchisee"]}><BillingSetup /></Protected>} />
          <Route path="/billing/comms" element={<Protected roles={["franchisee"]}><CommCenter /></Protected>} />
          <Route path="/billing/new/:kind" element={<Protected roles={["franchisee"]}><DocEditor /></Protected>} />
          <Route path="/billing/doc/:id" element={<Protected roles={["franchisee"]}><DocEditor /></Protected>} />
          <Route path="/billing/new-job" element={<Protected roles={["franchisee"]}><JobEditor /></Protected>} />
          <Route path="/billing/job/:id" element={<Protected roles={["franchisee"]}><JobEditor /></Protected>} />
          <Route path="/billing/calendar" element={<Protected roles={["franchisee"]}><BillingCalendar /></Protected>} />
          <Route path="/my-schedule" element={<Protected roles={["franchisee_employee"]}><MySchedule /></Protected>} />
          <Route path="/pay/:token" element={<PublicPay />} />
          <Route path="/co/:token" element={<PublicChangeOrder />} />
          <Route path="/request/:token" element={<RequestForm />} />
          <Route path="/admin/:section" element={<Protected roles={["admin"]}><AdminSection /></Protected>} />
          <Route path="/store" element={<Protected roles={BUYERS}><StoreIndex /></Protected>} />
          <Route path="/store/cart" element={<Protected roles={BUYERS}><StoreCart /></Protected>} />
          <Route path="/store/orders" element={<Protected roles={BUYERS}><StoreOrders /></Protected>} />
          <Route path="/store/:id" element={<Protected roles={BUYERS}><StoreProduct /></Protected>} />
          <Route path="/quickbooks" element={<Protected roles={["admin"]}><QuickBooks /></Protected>} />
          <Route path="/warranty" element={<Protected roles={[...GUIDE_ROLES, "machine_owner"]} feature="warranty"><Warranty /></Protected>} />
          <Route path="/insurance" element={<Protected roles={BUYERS}><Insurance /></Protected>} />
          <Route path="/safety" element={<Protected roles={GUIDE_ROLES} feature="safety_center"><SafetyCenter /></Protected>} />
          <Route path="/safety/jobsite-checklist" element={<Protected roles={BUYERS}><JobsiteChecklist /></Protected>} />
          <Route path="/safety/incident-report" element={<Protected roles={BUYERS}><IncidentReport /></Protected>} />
          <Route path="/community" element={<Protected roles={GUIDE_ROLES} feature="community"><Community /></Protected>} />
          <Route path="/community/:id" element={<Protected roles={GUIDE_ROLES} feature="community"><CommunityQuestion /></Protected>} />
          <Route path="/customer-community" element={<Protected roles={OWNERS}><CustomerCommunity /></Protected>} />
          <Route path="/customer-team" element={<Protected roles={OWNERS}><CustomerTeam /></Protected>} />
          <Route path="/sales-coach/quote-builder" element={<Protected roles={["admin", "franchisee", "franchisee_employee", "prospect"]} feature="quote_builder"><QuoteBuilder /></Protected>} />
          <Route path="/sales-coach/door-quote" element={<Protected roles={["admin", "franchisee", "franchisee_employee", "prospect"]} feature="quote_builder"><ProjectQuote /></Protected>} />
          <Route path="/employees" element={<Protected roles={["franchisee"]}><ManageEmployees /></Protected>} />
          <Route path="/guide" element={<Protected><Guide /></Protected>} />
          <Route path="/section/:key" element={<Protected><Section /></Protected>} />
          <Route path="/support" element={<Protected><Support /></Protected>} />
          <Route path="/manual-viewer" element={<Protected roles={GUIDE_ROLES}><ManualViewer /></Protected>} />
          <Route path="/training" element={<Protected><Training /></Protected>} />
          <Route path="/training-course" element={<Protected><TrainingCourse /></Protected>} />
          <Route path="/employee-onboarding" element={<Protected roles={["franchisee_employee", "admin"]}><EmployeeOnboarding /></Protected>} />
          <Route path="/launch-training" element={<Protected roles={BUYERS}><LaunchTraining /></Protected>} />
          <Route path="/launch-training/day/:num" element={<Protected roles={BUYERS}><LaunchDay /></Protected>} />
          <Route path="/feedback" element={<Protected><FeedbackFeed /></Protected>} />
          <Route path="/feedback/new" element={<Protected><NewFeedback /></Protected>} />
          <Route path="/quiz/:id" element={<Protected><QuizScreen /></Protected>} />
          <Route path="/health-check" element={<Protected roles={GUIDE_ROLES} feature="health_check"><HealthCheck /></Protected>} />
          <Route path="/lso-verify/:certNum" element={<LsoVerify />} />
          <Route path="/lso" element={<Protected roles={LSO_STAFF}><LsoHub /></Protected>} />
          <Route path="/lso/module/:num" element={<Protected roles={LSO_STAFF}><LsoModule /></Protected>} />
          <Route path="/lso/final-exam" element={<Protected roles={LSO_STAFF}><LsoFinalExam /></Protected>} />
          <Route path="/lso/certificate" element={<Protected roles={LSO_STAFF}><LsoCertificate /></Protected>} />
          <Route path="/lso/employees" element={<Protected roles={["franchisee", "admin"]}><LsoEmployees /></Protected>} />
          <Route path="/my-lso-cert" element={<Protected roles={["franchisee", "franchisee_employee", "admin"]}><MyLsoCert /></Protected>} />
          <Route path="/lso-rental" element={<Protected roles={RENTAL}><LsoRental /></Protected>} />
          <Route path="/lso-course" element={<Protected roles={RENTAL}><LsoHub kind="rental" /></Protected>} />
          <Route path="/lso-course/module/:num" element={<Protected roles={RENTAL}><LsoModule kind="rental" /></Protected>} />
          <Route path="/lso-course/final-exam" element={<Protected roles={RENTAL}><LsoFinalExam kind="rental" /></Protected>} />
          <Route path="/lso-course/certificate" element={<Protected roles={RENTAL}><LsoCertificate kind="rental" /></Protected>} />
          {Object.entries(ROLE_HOMES).map(([path, role]) => (
            <Route key={path} path={path} element={<Protected roles={[role, "admin"]}><ComingSoon roleHome /></Protected>} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </InsuranceGate>
        </DesktopShell>
        <GlobalAIFab />
        <HomeFloatingButton />
        <EmployeeRouteGuard />
        <SuspensionModal />
        <OutOfTerritoryModal />
        <HelpFab />
        <LsoTrainingBanner />
        </OAuthGate>
        <Toaster theme="dark" position="top-center" />
      </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
